import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { Database, Send, Eye, RefreshCw } from "lucide-react";

const DatabaseTest = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const { toast } = useToast();

  const testDatabaseRead = async () => {
    setIsLoading(true);
    try {
      console.log('Testing database read...');
      
      // Test 1: Count all messages
      const { count, error: countError } = await supabase
        .from('contact_messages_20251219_1800')
        .select('*', { count: 'exact', head: true });
      
      if (countError) {
        console.error('Count error:', countError);
        throw countError;
      }
      
      console.log('Total messages count:', count);
      
      // Test 2: Get recent messages
      const { data, error } = await supabase
        .from('contact_messages_20251219_1800')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) {
        console.error('Select error:', error);
        throw error;
      }
      
      console.log('Recent messages:', data);
      setMessages(data || []);
      
      // Test 3: Get stats by status
      const { data: statusData, error: statusError } = await supabase
        .from('contact_messages_20251219_1800')
        .select('status')
        .order('created_at', { ascending: false });
      
      if (statusError) {
        console.error('Status error:', statusError);
      } else {
        const statusCounts = (statusData || []).reduce((acc: any, msg: any) => {
          acc[msg.status] = (acc[msg.status] || 0) + 1;
          return acc;
        }, {});
        
        setStats({
          total: count || 0,
          byStatus: statusCounts
        });
      }
      
      toast({
        title: "Test réussi !",
        description: `${count || 0} messages trouvés dans la base de données`,
      });
      
    } catch (error: any) {
      console.error('Database test error:', error);
      toast({
        title: "Erreur de base de données",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const insertTestMessage = async () => {
    setIsLoading(true);
    try {
      console.log('Inserting test message...');
      
      const testMessage = {
        name: 'Test Admin',
        email: 'admin@test.com',
        phone: '079 123 45 67',
        company: 'Test Company',
        subject: 'Test depuis admin',
        message: `Message de test inséré le ${new Date().toLocaleString('fr-FR')}`,
        ip_address: 'admin-test',
        user_agent: 'Admin Test',
        source: 'admin-test'
      };
      
      const { data, error } = await supabase
        .from('contact_messages_20251219_1800')
        .insert([testMessage])
        .select()
        .single();
      
      if (error) {
        console.error('Insert error:', error);
        throw error;
      }
      
      console.log('Test message inserted:', data);
      
      toast({
        title: "Message de test inséré !",
        description: `ID: ${data.id}`,
      });
      
      // Refresh the list
      await testDatabaseRead();
      
    } catch (error: any) {
      console.error('Insert test error:', error);
      toast({
        title: "Erreur d'insertion",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Test Base de Données</h1>
            <p className="text-muted-foreground">
              Vérifiez directement les messages dans la base de données
            </p>
          </div>

          {/* Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Tests de base de données
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Button 
                  onClick={testDatabaseRead}
                  disabled={isLoading}
                  className="flex-1"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {isLoading ? 'Chargement...' : 'Lire les messages'}
                </Button>
                
                <Button 
                  onClick={insertTestMessage}
                  disabled={isLoading}
                  variant="outline"
                  className="flex-1"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {isLoading ? 'Insertion...' : 'Insérer message test'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          {stats && (
            <Card>
              <CardHeader>
                <CardTitle>Statistiques</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{stats.total}</div>
                    <div className="text-sm text-muted-foreground">Total</div>
                  </div>
                  {Object.entries(stats.byStatus || {}).map(([status, count]: [string, any]) => (
                    <div key={status} className="text-center">
                      <div className="text-2xl font-bold">{count}</div>
                      <div className="text-sm text-muted-foreground capitalize">{status}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Messages List */}
          {messages.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Messages récents ({messages.length})</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={testDatabaseRead}
                    disabled={isLoading}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div key={message.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium">{message.name}</h4>
                          <p className="text-sm text-muted-foreground">{message.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={message.status === 'nouveau' ? 'destructive' : 'secondary'}>
                            {message.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(message.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {message.subject && (
                        <p className="font-medium text-sm mb-1">{message.subject}</p>
                      )}
                      
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {message.message}
                      </p>
                      
                      <div className="mt-2 text-xs text-muted-foreground">
                        ID: {message.id} | Source: {message.source}
                        {message.phone && ` | Tél: ${message.phone}`}
                        {message.company && ` | Société: ${message.company}`}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Pour tester :</h4>
                  <ol className="text-sm text-muted-foreground mt-1 space-y-1 list-decimal list-inside">
                    <li>Cliquez "Lire les messages" pour voir les messages existants</li>
                    <li>Cliquez "Insérer message test" pour ajouter un message de test</li>
                    <li>Allez sur la page Contact pour envoyer un vrai message</li>
                    <li>Revenez ici pour vérifier que le message apparaît</li>
                  </ol>
                </div>
                
                <div>
                  <h4 className="font-medium">Si aucun message n'apparaît :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• La table est peut-être vide (normal si premier test)</li>
                    <li>• Problème de permissions RLS</li>
                    <li>• Erreur de nom de table</li>
                    <li>• Vérifiez la console (F12) pour les erreurs</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DatabaseTest;